import {asGatsbyImageDataAccess, getFirstSrcFromSources} from './gatsbyImageDataAccess';
import type {ImageDef, RasterImageDef, VectorImageDef} from './imageTypes';
import type {MicrositeImageResource, RasterMicrositeImageResource, RasterMicrositeImageVariantResource, VectorMicrositeImageResource} from '../types/imageResources';
import type {IGatsbyImageData} from 'gatsby-plugin-image';

export type MicrositeImageNode = {
  readonly name: string;
  readonly publicURL: string | null;
  readonly childImageSharp: {
    readonly defaultImage: IGatsbyImageData | null;
    readonly instagramCardImage: IGatsbyImageData | null;
    readonly heroDesktopImage: IGatsbyImageData | null;
    readonly heroMobileImage: IGatsbyImageData | null;
    readonly tinyAvatarImage: IGatsbyImageData | null;
  } | null;
};

export type MicrositeImageQueryResult = {
  readonly allFile: {
    readonly nodes: ReadonlyArray<MicrositeImageNode>;
  };
};

export type ImageLookups<ImageKey extends string> = {
  readonly defaultLookup: Map<ImageKey, IGatsbyImageData>;
  readonly instagramCardLookup: Map<ImageKey, IGatsbyImageData>;
  readonly heroDesktopLookup: Map<ImageKey, IGatsbyImageData>;
  readonly heroMobileLookup: Map<ImageKey, IGatsbyImageData>;
  readonly avatarTinyLookup: Map<ImageKey, IGatsbyImageData>;
  readonly vectorLookup: Map<ImageKey, string>;
};

const imageIdForDebug = <T extends string>(imageId: T): string => imageId;
const hasImageDefinition = <ImageKey extends string>(images: Record<ImageKey, ImageDef>, imageId: string): imageId is ImageKey => Object.prototype.hasOwnProperty.call(images, imageId);

export const buildImageLookups = <ImageKey extends string>(nodes: ReadonlyArray<MicrositeImageNode>, images: Record<ImageKey, ImageDef>): ImageLookups<ImageKey> => {
  const defaultLookup = new Map<ImageKey, IGatsbyImageData>();
  const instagramCardLookup = new Map<ImageKey, IGatsbyImageData>();
  const heroDesktopLookup = new Map<ImageKey, IGatsbyImageData>();
  const heroMobileLookup = new Map<ImageKey, IGatsbyImageData>();
  const avatarTinyLookup = new Map<ImageKey, IGatsbyImageData>();
  const vectorLookup = new Map<ImageKey, string>();

  for (const node of nodes) {
    if (!hasImageDefinition(images, node.name)) {
      continue;
    }
    const imageKey = node.name;
    const imageDef = images[imageKey];

    if (imageDef.kind === `vector`) {
      if (!node.publicURL) {
        throw new Error(`Missing public URL for vector microsite asset "${imageIdForDebug(imageKey)}". Ensure the SVG lives in src/assets/images and Gatsby picked it up.`);
      }
      vectorLookup.set(imageKey, node.publicURL);
      continue;
    }

    if (!node.childImageSharp) {
      throw new Error(`Gatsby image data missing for raster asset "${imageIdForDebug(imageKey)}".`);
    }

    if (node.childImageSharp.defaultImage) {
      defaultLookup.set(imageKey, node.childImageSharp.defaultImage);
    }

    if (node.childImageSharp.instagramCardImage) {
      instagramCardLookup.set(imageKey, node.childImageSharp.instagramCardImage);
    }

    if (node.childImageSharp.heroDesktopImage) {
      heroDesktopLookup.set(imageKey, node.childImageSharp.heroDesktopImage);
    }

    if (node.childImageSharp.heroMobileImage) {
      heroMobileLookup.set(imageKey, node.childImageSharp.heroMobileImage);
    }

    if (node.childImageSharp.tinyAvatarImage) {
      avatarTinyLookup.set(imageKey, node.childImageSharp.tinyAvatarImage);
    }
  }

  return {
    defaultLookup,
    instagramCardLookup,
    heroDesktopLookup,
    heroMobileLookup,
    avatarTinyLookup,
    vectorLookup,
  };
};

type CreateMicrositeImageHooksOptions<ImageKey extends string> = {
  readonly images: Record<ImageKey, ImageDef>;
  readonly useLookups: () => ImageLookups<ImageKey>;
};

const buildVariantResource = <ImageKey extends string>(imageId: ImageKey, lookup: Map<ImageKey, IGatsbyImageData>, variantLabel: string): RasterMicrositeImageVariantResource => {
  const variantData = lookup.get(imageId);

  if (!variantData) {
    throw new Error(`Image "${imageId}" is missing the required ${variantLabel} variant.`);
  }

  const parsedVariantData = asGatsbyImageDataAccess(variantData);
  const fallbackSrc = parsedVariantData.images.fallback?.src ?? getFirstSrcFromSources(parsedVariantData.images.sources) ?? null;

  if (!fallbackSrc) {
    throw new Error(`Unable to derive fallback src for ${variantLabel} variant of raster asset "${imageId}".`);
  }

  return {
    gatsbyImageData: variantData,
    fallbackSrc,
    placeholderSrc: parsedVariantData.placeholder?.fallback ?? null,
  };
};

const ensureRasterResource = <ImageKey extends string>(imageId: ImageKey, def: RasterImageDef, baseLookup: Map<ImageKey, IGatsbyImageData>, lookups: ImageLookups<ImageKey>): RasterMicrositeImageResource => {
  const gatsbyImageData = baseLookup.get(imageId);

  if (!gatsbyImageData) {
    throw new Error(`Gatsby image data missing for raster asset "${imageId}". Ensure the file exists in src/assets/images.`);
  }

  const parsedImageData = asGatsbyImageDataAccess(gatsbyImageData);
  const fallbackSrc = parsedImageData.images.fallback?.src ?? getFirstSrcFromSources(parsedImageData.images.sources) ?? null;

  if (!fallbackSrc) {
    throw new Error(`Unable to derive fallback src for raster asset "${imageId}".`);
  }

  return {
    kind: `raster`,
    description: def.description,
    gatsbyImageData,
    fallbackSrc,
    placeholderSrc: parsedImageData.placeholder?.fallback ?? null,
    variants: {
      heroDesktop: buildVariantResource(imageId, lookups.heroDesktopLookup, `heroDesktop`),
      heroMobile: buildVariantResource(imageId, lookups.heroMobileLookup, `heroMobile`),
      avatarTiny: buildVariantResource(imageId, lookups.avatarTinyLookup, `avatarTiny`),
    },
  };
};

const ensureVectorResource = <ImageKey extends string>(imageId: ImageKey, def: VectorImageDef, vectorLookup: Map<ImageKey, string>): VectorMicrositeImageResource => {
  const url = vectorLookup.get(imageId);
  if (!url) {
    throw new Error(`Vector asset "${imageId}" is missing from the Gatsby public URL lookup. Never import SVGs manually—ensure the file is inside src/assets/images and rerun the build.`);
  }

  return {
    kind: `vector`,
    description: def.description,
    url,
  };
};

export const createMicrositeImageHooks = <ImageKey extends string>({images, useLookups}: CreateMicrositeImageHooksOptions<ImageKey>) => {
  const useMicrositeImage = (imageId: ImageKey): MicrositeImageResource => {
    if (!hasImageDefinition(images, imageId)) {
      throw new Error(`Requested image "${imageId}" is not defined in images.ts.`);
    }
    const imageDef = images[imageId];
    const lookups = useLookups();

    if (imageDef.kind === `vector`) {
      return ensureVectorResource(imageId, imageDef, lookups.vectorLookup);
    }

    return ensureRasterResource(imageId, imageDef, lookups.defaultLookup, lookups);
  };

  const useFauxInstagramImage = (imageId: ImageKey): MicrositeImageResource => {
    if (!hasImageDefinition(images, imageId)) {
      throw new Error(`Requested image "${imageId}" is not defined in images.ts.`);
    }
    const imageDef = images[imageId];
    const lookups = useLookups();

    if (imageDef.kind === `vector`) {
      throw new Error(`Faux Instagram cards require raster assets. "${imageId}" is defined as vector.`);
    }

    return ensureRasterResource(imageId, imageDef, lookups.instagramCardLookup, lookups);
  };

  return {
    useMicrositeImage,
    useFauxInstagramImage,
  };
};
